/**
 * Ground pickups for the bird game:
 *   reward flowers (medium) — grow from soil, bonus score (morph into apple on collect)
 */

import { drawApple } from './groundCreatures';

const FLOWER_PETALS = [
  [255, 110, 130],
  [255, 195, 70],
  [200, 120, 255],
  [255, 150, 90],
  [110, 190, 255],
];

function easeOut(t) {
  return 1 - (1 - t) * (1 - t);
}

function easeIn(t) {
  return t * t;
}

export function createRewardFlower(boardW, boardH, opts) {
  const options = opts || {};
  const columns = options.columns || 5;
  const left = boardW * 0.18;
  const right = boardW * 0.82;
  const gap = (right - left) / Math.max(1, columns - 1);
  const col =
    options.columnIndex != null
      ? options.columnIndex % columns
      : Math.floor(Math.random() * columns);
  const x = left + gap * col + (Math.random() - 0.5) * gap * 0.2;
  const groundY =
    options.groundY != null
      ? options.groundY
      : boardH * 0.88;
  return {
    kind: 'flower',
    x,
    groundY,
    column: col,
    height: 38 + Math.random() * 16,
    progress: 0,
    state: 'emerging',
    stateAt: Date.now(),
    phase: Math.random() * Math.PI * 2,
    hit: false,
    color: FLOWER_PETALS[Math.floor(Math.random() * FLOWER_PETALS.length)],
    sparkle: 0,
    appleLift: 0,
  };
}

/** Same lifecycle shape as creatures so spawn/timing stays familiar. */
export function tickRewardFlower(f, cfg) {
  const { emergeMs, visibleMs, now, reducedMotion } = cfg;
  const elapsed = now - f.stateAt;

  if (f.state === 'emerging') {
    const t = Math.min(1, elapsed / emergeMs);
    f.progress = reducedMotion ? t : easeOut(t);
    if (t >= 1) {
      f.progress = 1;
      f.state = 'idle';
      f.stateAt = now;
    }
  } else if (f.state === 'idle') {
    if (elapsed >= visibleMs) {
      f.state = 'burrowing';
      f.stateAt = now;
    }
  } else if (f.state === 'happy') {
    // Morph into apple food, then vanish (same beat as worms)
    f.sparkle = Math.min(1, elapsed / 300);
    f.appleLift = easeOut(Math.min(1, elapsed / 650)) * 42;
    if (elapsed >= 720) {
      f.progress = 0;
      f.state = 'gone';
    }
  } else if (f.state === 'burrowing') {
    const t = Math.min(1, elapsed / (emergeMs * 0.7));
    f.progress = 1 - (reducedMotion ? t : easeIn(t));
    if (t >= 1) {
      f.progress = 0;
      f.state = 'gone';
    }
  }
}

export function flowerTipY(f) {
  return f.groundY - f.height * f.progress;
}

export function isNearRewardFlower(birdCX, birdCY, f, pad) {
  if (f.hit || f.state === 'gone') return false;
  if (f.progress < 0.35) return false;
  if (f.state === 'burrowing' && f.progress < 0.4) return false;
  const tip = flowerTipY(f);
  const midY = (f.groundY + tip) * 0.55;
  return (
    Math.hypot(birdCX - f.x, birdCY - tip) <= pad ||
    Math.hypot(birdCX - f.x, birdCY - midY) <= pad
  );
}

export function drawRewardFlower(p, f, frameCount) {
  if (f.state === 'gone') return;
  if (f.progress <= 0.02 && f.state !== 'happy') return;

  const tip = flowerTipY(f);
  const morph = f.state === 'happy' ? Math.min(1, f.sparkle || 0) : 0;
  const lift = f.state === 'happy' ? f.appleLift || 0 : 0;
  const sway =
    Math.sin(frameCount * 0.05 + f.phase) * 3 * f.progress * (1 - morph);
  const stemTopX = f.x + sway;
  const stemTopY = tip;
  const [pr, pg, pb] = f.color;
  const fade = 1 - morph;

  p.push();

  // Flower fades out as it becomes food
  if (morph < 0.95 && f.progress > 0.02) {
    p.stroke(50, 130, 70, Math.floor(220 * f.progress * fade));
    p.strokeWeight(2.5);
    p.line(f.x, f.groundY, stemTopX, stemTopY);
    p.noStroke();
    p.fill(60, 150, 80, Math.floor(200 * f.progress * fade));
    const leafY = f.groundY - f.height * f.progress * 0.45;
    p.ellipse(f.x - 8 + sway * 0.4, leafY, 12, 6);
    p.ellipse(f.x + 9 + sway * 0.4, leafY + 3, 11, 5);

    const bloomR = 5 + 4 * f.progress;
    for (let i = 0; i < 5; i += 1) {
      const a = (Math.PI * 2 * i) / 5 + f.phase;
      p.fill(pr, pg, pb, Math.floor(210 * f.progress * fade));
      p.circle(
        stemTopX + Math.cos(a) * bloomR * 0.75,
        stemTopY + Math.sin(a) * bloomR * 0.75,
        bloomR
      );
    }
    p.fill(255, 230, 110, Math.floor(230 * f.progress * fade));
    p.circle(stemTopX, stemTopY, bloomR * 0.9);

    p.fill(40, 120, 60, Math.floor(180 * f.progress * fade));
    p.triangle(f.x - 7, f.groundY, f.x - 2, f.groundY - 10, f.x + 1, f.groundY);
    p.triangle(f.x + 6, f.groundY, f.x + 2, f.groundY - 9, f.x - 1, f.groundY);
  }

  // Apple pops in at the bloom and floats upward (same as worms)
  if (morph > 0.05) {
    const appleSize = 22 + morph * 16;
    const ax = f.x;
    const ay = tip - lift - morph * 8;
    drawApple(p, ax, ay, appleSize, Math.min(1, morph * 1.2));
    if (morph > 0.4) {
      p.noStroke();
      p.fill(255, 230, 120, Math.floor(180 * (1 - morph * 0.3)));
      for (let s = 0; s < 5; s += 1) {
        const a = f.phase + s * 1.25 + frameCount * 0.1;
        const rad = 16 + morph * 14;
        p.circle(ax + Math.cos(a) * rad, ay + Math.sin(a) * rad, 3);
      }
    }
  }

  p.pop();
}
